import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";

// FieldNamePicker — for choosing which variable to WRITE TO (Set Data, Ask Question)
// Different from VariablePicker which READS/INSERTS <<variable>> syntax into text.
// This outputs the raw variable name (e.g. "customer_tier"), not "<<customer_tier>>".

interface FieldOption {
  name: string;
  label: string;
  scope: "built-in" | "contact" | "conversation" | "workflow";
}

const BUILT_IN_FIELDS: FieldOption[] = [
  { name: "name",      label: "Full Name",        scope: "built-in" },
  { name: "firstName", label: "First Name",       scope: "built-in" },
  { name: "lastName",  label: "Last Name",        scope: "built-in" },
  { name: "email",     label: "Email",            scope: "built-in" },
  { name: "phone",     label: "Phone",            scope: "built-in" },
  { name: "language",  label: "Language",         scope: "built-in" },
];

const SCOPE_LABELS: Record<string, string> = {
  "built-in": "Built-in",
  contact:     "Contact",
  conversation:"Conversation",
  workflow:    "Workflow",
};

const SCOPE_COLORS: Record<string, string> = {
  "built-in":  "bg-slate-100 text-slate-600",
  contact:     "bg-blue-100 text-blue-700",
  conversation:"bg-amber-100 text-amber-700",
  workflow:    "bg-green-100 text-green-700",
};

const SCOPE_ORDER = ["built-in", "contact", "conversation", "workflow"] as const;

interface FieldNamePickerProps {
  value: string;
  onChange: (name: string) => void;
  /** If set, only show fields in these scopes (plus built-ins). Empty = show all. */
  scopes?: string[];
  placeholder?: string;
}

export default function FieldNamePicker({
  value,
  onChange,
  scopes,
  placeholder
}: FieldNamePickerProps) {
  const [allFields, setAllFields] = useState<FieldOption[]>(BUILT_IN_FIELDS);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const api = useApi();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.messaging.get("ContactFieldDefinitions/list");
        if (res.ok) {
          const data = await res.json();
          const defs = Array.isArray(data)
            ? data
            : Array.isArray(data?.definitions)
              ? data.definitions
              : [];
          const custom: FieldOption[] = defs
            .filter((d: { name?: string }) => d.name)
            .map((d: { name?: string; label?: string; scope?: string }) => ({
              name:  d.name  || "",
              label: d.label || d.name || "",
              scope: (d.scope as FieldOption["scope"]) || "conversation",
            }));
          setAllFields([...BUILT_IN_FIELDS, ...custom]);
        }
      } catch {
        // keep built-ins only
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // Scope filter: always include built-ins; filter custom fields by requested scopes
  const scopeFiltered = scopes?.length
    ? allFields.filter(f => f.scope === "built-in" || scopes.includes(f.scope))
    : allFields;

  // Search filter
  const filtered = search.trim()
    ? scopeFiltered.filter(
        f =>
          f.name.toLowerCase().includes(search.toLowerCase()) ||
          f.label.toLowerCase().includes(search.toLowerCase())
      )
    : scopeFiltered;

  const currentField = allFields.find(f => f.name === value);
  const isKnownField = !!currentField;
  const effectiveManual = manualMode || (!!value && !isKnownField);

  function selectField(f: FieldOption) {
    onChange(f.name);
    setOpen(false);
    setSearch("");
    setManualMode(false);
  }

  if (effectiveManual) {
    return (
      <div className="flex gap-1 items-center">
        <input
          type="text"
          placeholder={placeholder ?? "Variable name (e.g. customer_tier)"}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="border rounded-md p-2 flex-1 text-sm"
        />
        <button
          type="button"
          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
          onClick={() => { setManualMode(false); setOpen(true); }}
        >
          Pick field
        </button>
      </div>
    );
  }

  const displayLabel = currentField
    ? currentField.label
    : value || "";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="w-full text-left border rounded-md p-2 text-sm bg-white hover:border-blue-400 flex items-center justify-between gap-2"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {displayLabel ? (
            <>
              <code className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-mono text-xs shrink-0">
                {`<<${value}>>`}
              </code>
              <span className="text-slate-700 text-xs truncate">{currentField?.label}</span>
              {currentField && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${SCOPE_COLORS[currentField.scope]}`}>
                  {SCOPE_LABELS[currentField.scope]}
                </span>
              )}
            </>
          ) : (
            <span className="text-slate-400">
              {placeholder ?? "Select or create a field..."}
            </span>
          )}
        </div>
        <svg
          className="w-4 h-4 text-slate-400 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-lg z-50 flex flex-col max-h-64">
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              type="text"
              placeholder="Search fields..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Scope-grouped list */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="p-3 text-sm text-slate-400 text-center">No fields found</div>
            )}
            {SCOPE_ORDER.map(scope => {
              const items = filtered.filter(f => f.scope === scope);
              if (items.length === 0) return null;
              return (
                <div key={scope}>
                  <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                    {SCOPE_LABELS[scope]}
                  </div>
                  {items.map(f => (
                    <button
                      key={f.name}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                      onClick={() => selectField(f)}
                    >
                      <code className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                        {`<<${f.name}>>`}
                      </code>
                      <span className="text-sm text-slate-700 truncate flex-1">{f.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${SCOPE_COLORS[scope]}`}>
                        {SCOPE_LABELS[scope]}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 rounded-b-lg flex items-center justify-between">
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-blue-600 hover:underline"
              onClick={() => { setOpen(false); setManualMode(true); }}
            >
              Type name manually...
            </button>
            <Link
              to="/contact_fields"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setOpen(false)}
            >
              + Create custom field
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
